import { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Button, Radio, RadioGroup, FormControlLabel, FormControl,
    FormLabel, CircularProgress, Paper, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { getQuestions, submitQuiz, getMyStatus } from '../../api/quizApi';
import { useUser } from '../../context/userContext';

const PP = "'Poppins', sans-serif";
// Mirrors QuizRepository.PassRatio — the server is the authority on pass/fail;
// these two only drive the copy. Derived rather than hardcoded so the count and
// the percentage cannot disagree.
const PASS_RATIO = 0.6;
const TOTAL_QUESTIONS = 40;
const PASS_THRESHOLD = Math.ceil(TOTAL_QUESTIONS * PASS_RATIO);
const PASS_PERCENT = Math.round(PASS_RATIO * 100);

// EN falls back to the CN text when a question has no English translation, so a
// half-translated bank never renders a blank line.
function pickLang(viewLang, cn, en) {
    return viewLang === 'en' ? (en || cn) : cn;
}

// Fisher-Yates shuffle — used to randomise question display order once per load.
function shuffleArray(arr) {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
}

export default function QuizPage() {
    const { setUser } = useUser();
    const [questions, setQuestions] = useState([]);
    const [myStatus, setMyStatus] = useState(null);
    const [answers, setAnswers] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    // No global language/i18n context exists elsewhere (TutorialsReader.jsx keeps its own
    // local viewLang state too, default 'en') — so this page defaults the same way.
    const [viewLang, setViewLang] = useState('en');

    const loadQuiz = useCallback(async () => {
        setLoading(true);
        setError('');
        setResult(null);
        setAnswers({});
        try {
            const [qRes, statusRes] = await Promise.all([
                getQuestions(),
                getMyStatus().catch(() => ({ data: null })),
            ]);
            setQuestions(shuffleArray(qRes.data || []));
            setMyStatus(statusRes?.data || null);
        } catch {
            setError('Failed to load the quiz. Please try again.');
            setQuestions([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadQuiz(); }, [loadQuiz]);

    const handleAnswer = (questionId, value) => {
        setAnswers((prev) => ({ ...prev, [questionId]: value }));
    };

    const allAnswered = questions.length > 0 && questions.every((q) => !!answers[q.quizQuestionId]);

    const handleSubmit = async () => {
        if (!allAnswered || submitting) return;
        setSubmitting(true);
        setError('');
        try {
            const r = await submitQuiz(answers);
            setResult(r.data);
            // Passing opens the app immediately — no second login. The server already
            // treats the pass as authoritative (SalesController + TrainingGateMiddleware
            // read the attempt, not this flag); this just catches the UI up so the menu
            // and the route guard stop showing the training room. (user 2026-09-24)
            if (r.data?.passed) {
                try { localStorage.setItem('CanCreateSale', 'true'); } catch { /* private mode */ }
                if (setUser) setUser((prev) => (prev ? { ...prev, canCreateSale: true } : prev));
            }
            // Refresh MyStatus in the background so a subsequent visit reflects the new pass state.
            try {
                const statusRes = await getMyStatus();
                setMyStatus(statusRes.data);
            } catch { /* non-fatal — result panel already has the outcome */ }
        } catch {
            setError('Failed to submit your answers. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleRetake = () => { loadQuiz(); };

    if (loading) {
        return (
            <Box sx={{ p: 4, textAlign: 'center', fontFamily: PP }}>
                <CircularProgress size={28} />
                <Typography sx={{ fontFamily: PP, mt: 2, color: '#64748B', fontSize: '0.85rem' }}>
                    Loading quiz...
                </Typography>
            </Box>
        );
    }

    if (error && questions.length === 0) {
        return (
            <Box sx={{ p: 4, textAlign: 'center', fontFamily: PP }}>
                <Typography sx={{ fontFamily: PP, color: '#EF4444', fontWeight: 600, fontSize: '0.9rem' }}>
                    {error}
                </Typography>
                <Button
                    onClick={loadQuiz}
                    variant="outlined"
                    sx={{ mt: 2, fontFamily: PP, textTransform: 'none', borderRadius: '8px', fontWeight: 600 }}
                >
                    Retry
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ fontFamily: PP, width: '100%', px: { xs: 2, md: 4 }, pb: 4 }}>
            {/* Header */}
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
                <Box>
                    <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#0F172A', fontFamily: PP }}>
                        Quiz
                    </Typography>
                    <Typography sx={{ fontSize: '0.85rem', color: '#64748B', fontFamily: PP, mt: 0.5 }}>
                        {TOTAL_QUESTIONS} questions. You need at least {PASS_THRESHOLD} correct ({PASS_PERCENT}%) to pass.
                    </Typography>
                </Box>
                <ToggleButtonGroup
                    value={viewLang}
                    exclusive
                    onChange={(_, v) => { if (v) setViewLang(v); }}
                    size="small"
                    sx={{
                        '& .MuiToggleButton-root': {
                            fontFamily: PP,
                            fontSize: '0.72rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            px: 1.5,
                            py: 0.4,
                            border: '1px solid #E2E8F0',
                            color: '#64748B',
                            '&.Mui-selected': {
                                color: '#0F62CC',
                                bgcolor: '#E0EFFF',
                                borderColor: '#1A8CFF',
                            },
                        },
                    }}
                >
                    <ToggleButton value="en">EN</ToggleButton>
                    <ToggleButton value="cn">中文</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {/* Already-passed banner — still allows retaking */}
            {myStatus?.passed && !result && (
                <Box
                    sx={{
                        display: 'flex', alignItems: 'center', gap: 1, mb: 2.5, p: 1.5,
                        borderRadius: '10px', bgcolor: '#F0FDF4', border: '1px solid #BBF7D0',
                    }}
                >
                    <CheckCircleIcon sx={{ color: '#16A34A', fontSize: 22, flexShrink: 0 }} />
                    <Typography sx={{ fontFamily: PP, fontSize: '0.85rem', fontWeight: 600, color: '#15803D' }}>
                        You've passed the RSC certification. You can retake the quiz any time to improve your score.
                    </Typography>
                </Box>
            )}

            {error && (
                <Typography sx={{ fontFamily: PP, fontSize: '0.82rem', color: '#EF4444', mb: 2 }}>
                    {error}
                </Typography>
            )}

            {result ? (
                <ResultPanel result={result} viewLang={viewLang} onRetake={handleRetake} />
            ) : (
                <>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {questions.map((q, idx) => (
                            <QuestionCard
                                key={q.quizQuestionId}
                                index={idx + 1}
                                question={q}
                                viewLang={viewLang}
                                value={answers[q.quizQuestionId] || ''}
                                onChange={(val) => handleAnswer(q.quizQuestionId, val)}
                            />
                        ))}
                    </Box>

                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.5 }}>
                        <Typography sx={{ fontFamily: PP, fontSize: '0.8rem', color: '#94A3B8' }}>
                            {Object.keys(answers).length} / {questions.length} answered
                        </Typography>
                        <Button
                            variant="contained"
                            disabled={!allAnswered || submitting}
                            onClick={handleSubmit}
                            sx={{
                                fontFamily: PP, fontWeight: 600, textTransform: 'none', borderRadius: '8px',
                                boxShadow: 'none', background: 'linear-gradient(135deg,#1A8CFF,#0F62CC)',
                                '&.Mui-disabled': { background: '#E2E8F0', color: '#94A3B8' },
                            }}
                        >
                            {submitting ? 'Submitting...' : 'Submit'}
                        </Button>
                    </Box>
                </>
            )}
        </Box>
    );
}

function QuestionCard({ index, question, viewLang, value, onChange }) {
    const resolve = (cn, en) => pickLang(viewLang, cn, en);
    const options = [
        { key: 'A', text: resolve(question.optionA, question.optionAEn) },
        { key: 'B', text: resolve(question.optionB, question.optionBEn) },
        { key: 'C', text: resolve(question.optionC, question.optionCEn) },
        { key: 'D', text: resolve(question.optionD, question.optionDEn) },
    ];
    const questionText = resolve(question.questionText, question.questionTextEn);

    return (
        <Paper
            variant="outlined"
            sx={{ p: 2, borderRadius: '12px', borderColor: '#E2E8F0', boxShadow: '0 1px 4px rgba(15,23,42,0.05)' }}
        >
            <FormControl component="fieldset" sx={{ width: '100%' }}>
                <FormLabel
                    component="legend"
                    sx={{ fontFamily: PP, fontSize: '0.92rem', fontWeight: 600, color: '#0F172A', mb: 1, '&.Mui-focused': { color: '#0F172A' } }}
                >
                    {index}. {questionText}
                </FormLabel>
                <RadioGroup value={value} onChange={(e) => onChange(e.target.value)}>
                    {options.map((opt) => (
                        <FormControlLabel
                            key={opt.key}
                            value={opt.key}
                            control={<Radio size="small" />}
                            label={
                                <Typography sx={{ fontFamily: PP, fontSize: '0.85rem', color: '#334155' }}>
                                    {opt.key}. {opt.text}
                                </Typography>
                            }
                            sx={{ ml: 0, mb: 0.25 }}
                        />
                    ))}
                </RadioGroup>
            </FormControl>
        </Paper>
    );
}

function ResultPanel({ result, viewLang, onRetake }) {
    const { score, total, passed, review } = result || {};
    const wrong = review || [];
    return (
        <Box sx={{ py: 4 }}>
            <Box sx={{ textAlign: 'center' }}>
                {passed ? (
                    <CheckCircleIcon sx={{ fontSize: 72, color: '#16A34A', mb: 1 }} />
                ) : (
                    <CancelIcon sx={{ fontSize: 72, color: '#EF4444', mb: 1 }} />
                )}
                <Typography sx={{ fontFamily: PP, fontSize: '1.4rem', fontWeight: 800, color: '#0F172A' }}>
                    {score} / {total}
                </Typography>
                <Typography
                    sx={{ fontFamily: PP, fontSize: '1rem', fontWeight: 600, color: passed ? '#15803D' : '#B91C1C', mt: 0.5 }}
                >
                    {passed ? 'You passed!' : 'You did not pass this time.'}
                </Typography>
                <Typography sx={{ fontFamily: PP, fontSize: '0.85rem', color: '#64748B', mt: 1.5, maxWidth: 480, mx: 'auto' }}>
                    {passed
                        ? 'Certified — the full system is open to you now. Create Sale, the calculators and your commission pages are in the menu.'
                        : `You need at least ${PASS_THRESHOLD} correct answers to pass. Review your answers below, then try again.`}
                </Typography>
                <Button
                    onClick={onRetake}
                    variant="outlined"
                    sx={{ mt: 3, fontFamily: PP, textTransform: 'none', borderRadius: '8px', fontWeight: 600 }}
                >
                    Retake
                </Button>
            </Box>

            {wrong.length > 0 && (
                <Box sx={{ mt: 5 }}>
                    <Typography sx={{ fontFamily: PP, fontSize: '1rem', fontWeight: 700, color: '#0F172A' }}>
                        Review your answers
                    </Typography>
                    <Typography sx={{ fontFamily: PP, fontSize: '0.82rem', color: '#64748B', mt: 0.5, mb: 2 }}>
                        {wrong.length === 1
                            ? 'You got 1 question wrong. The correct answer is marked in green.'
                            : `You got ${wrong.length} questions wrong. The correct answers are marked in green.`}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {wrong.map((item, idx) => (
                            <ReviewCard
                                key={item.question?.quizQuestionId ?? idx}
                                index={idx + 1}
                                item={item}
                                viewLang={viewLang}
                            />
                        ))}
                    </Box>
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                        <Button
                            onClick={onRetake}
                            variant="contained"
                            sx={{
                                fontFamily: PP, fontWeight: 600, textTransform: 'none', borderRadius: '8px',
                                boxShadow: 'none', background: 'linear-gradient(135deg,#1A8CFF,#0F62CC)',
                            }}
                        >
                            Retake quiz
                        </Button>
                    </Box>
                </Box>
            )}
        </Box>
    );
}

// One missed question: the four options with the user's own pick called out in red
// and the right one in green, plus the bank's explanation when it has one.
function ReviewCard({ index, item, viewLang }) {
    const q = item.question || {};
    const resolve = (cn, en) => pickLang(viewLang, cn, en);
    const options = [
        { key: 'A', text: resolve(q.optionA, q.optionAEn) },
        { key: 'B', text: resolve(q.optionB, q.optionBEn) },
        { key: 'C', text: resolve(q.optionC, q.optionCEn) },
        { key: 'D', text: resolve(q.optionD, q.optionDEn) },
    ];

    return (
        <Paper
            variant="outlined"
            sx={{ p: 2, borderRadius: '12px', borderColor: '#FECACA', boxShadow: '0 1px 4px rgba(15,23,42,0.05)' }}
        >
            <Typography sx={{ fontFamily: PP, fontSize: '0.92rem', fontWeight: 600, color: '#0F172A', mb: 1.25 }}>
                {index}. {resolve(q.questionText, q.questionTextEn)}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {options.map((opt) => {
                    const isCorrect = opt.key === item.correctOption;
                    const isYours = opt.key === item.yourAnswer;
                    return (
                        <Box
                            key={opt.key}
                            sx={{
                                display: 'flex', alignItems: 'flex-start', gap: 1,
                                px: 1.25, py: 0.75, borderRadius: '8px',
                                bgcolor: isCorrect ? '#F0FDF4' : isYours ? '#FEF2F2' : 'transparent',
                                border: '1px solid',
                                borderColor: isCorrect ? '#BBF7D0' : isYours ? '#FECACA' : 'transparent',
                            }}
                        >
                            {isCorrect ? (
                                <CheckCircleIcon sx={{ fontSize: 18, color: '#16A34A', mt: '1px', flexShrink: 0 }} />
                            ) : isYours ? (
                                <CancelIcon sx={{ fontSize: 18, color: '#EF4444', mt: '1px', flexShrink: 0 }} />
                            ) : (
                                <Box sx={{ width: 18, flexShrink: 0 }} />
                            )}
                            <Typography
                                sx={{
                                    fontFamily: PP, fontSize: '0.85rem', flex: 1,
                                    color: isCorrect ? '#15803D' : isYours ? '#B91C1C' : '#64748B',
                                    fontWeight: isCorrect || isYours ? 600 : 400,
                                }}
                            >
                                {opt.key}. {opt.text}
                            </Typography>
                            {(isCorrect || isYours) && (
                                <Typography
                                    sx={{
                                        fontFamily: PP, fontSize: '0.68rem', fontWeight: 700, whiteSpace: 'nowrap',
                                        letterSpacing: '0.03em', textTransform: 'uppercase', mt: '3px',
                                        color: isCorrect ? '#16A34A' : '#EF4444',
                                    }}
                                >
                                    {isCorrect ? 'Correct answer' : 'Your answer'}
                                </Typography>
                            )}
                        </Box>
                    );
                })}
            </Box>

            {!item.yourAnswer && (
                <Typography sx={{ fontFamily: PP, fontSize: '0.78rem', color: '#B91C1C', mt: 1 }}>
                    You did not answer this question.
                </Typography>
            )}

            {item.explanation && (
                <Box sx={{ mt: 1.25, p: 1.25, borderRadius: '8px', bgcolor: '#F8FAFC', border: '1px solid #E2E8F0' }}>
                    <Typography sx={{ fontFamily: PP, fontSize: '0.7rem', fontWeight: 700, color: '#64748B', letterSpacing: '0.03em', textTransform: 'uppercase', mb: 0.25 }}>
                        Why
                    </Typography>
                    <Typography sx={{ fontFamily: PP, fontSize: '0.82rem', color: '#334155' }}>
                        {item.explanation}
                    </Typography>
                </Box>
            )}
        </Paper>
    );
}
